/**
 * Trades Panel Component
 * Displays trades created by an AI system with summary stats
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Pagination,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ShowChartIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { aiSystemApi } from '../../services/api';

interface TradesPanelProps {
  systemId: number;
}

const shortExecutionId = (id?: string | null): string => {
  if (!id) return '-';
  return id.length > 12 ? `${id.slice(0, 8)}...${id.slice(-4)}` : id;
};

const ExecutionLinkChip: React.FC<{ id?: string | null }> = ({ id }) => {
  if (!id) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  return (
    <Chip
      label={shortExecutionId(id)}
      size="small"
      variant="outlined"
      component={RouterLink}
      to={`/ai/executions/${id}`}
      clickable
    />
  );
};

const TradesPanel: React.FC<TradesPanelProps> = ({ systemId }) => {
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [page, setPage] = useState(1);
  const limit = 25;

  const { data, isLoading, error } = useQuery({
    queryKey: ['ai-system-trades', systemId, statusFilter, page],
    queryFn: () => aiSystemApi.getTrades(systemId, {
      status: statusFilter,
      limit,
      offset: (page - 1) * limit,
    }),
    enabled: !!systemId,
    staleTime: 30000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load trades: {(error as Error).message}
      </Alert>
    );
  }

  const trades = data?.trades || [];
  const summary = data?.summary;
  const totalPages = Math.ceil((data?.total_count || 0) / limit);

  return (
    <Box sx={{ p: 3 }}>
      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShowChartIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Trades
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  {summary.total_trades}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summary.open_trades} open, {summary.closed_trades} closed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color={summary.total_pl >= 0 ? 'success' : 'error'} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Total P/L
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ mt: 1 }}
                  color={summary.total_pl >= 0 ? 'success.main' : 'error.main'}
                >
                  ${summary.total_pl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="success" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Win Rate
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  {summary.win_rate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summary.winning_trades}W / {summary.losing_trades}L / {summary.breakeven_trades}BE
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDownIcon color="info" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Closed Trades
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  {summary.closed_trades}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Out of {summary.total_trades} total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Trades</Typography>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, value) => {
            if (value) {
              setStatusFilter(value);
              setPage(1); // Reset to page 1 when filter changes
            }
          }}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="open">Open</ToggleButton>
          <ToggleButton value="closed">Closed</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Trades Table */}
      {trades.length === 0 ? (
        <Alert severity="info">
          No trades found for this AI system. Trades will appear here when the system opens positions.
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Direction</TableCell>
                  <TableCell align="right">Lot Size</TableCell>
                  <TableCell align="right">Entry</TableCell>
                  <TableCell align="right">SL/TP</TableCell>
                  <TableCell align="right">P/L</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Open Exec</TableCell>
                  <TableCell>Close Exec</TableCell>
                  <TableCell>Label</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trades.map((trade) => (
                  <TableRow key={trade.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(trade.created_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(trade.created_at).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {trade.instrument}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={trade.direction}
                        size="small"
                        color={trade.direction === 'BUY' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{trade.lot_size}</TableCell>
                    <TableCell align="right">{trade.entry_price}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {trade.stop_loss ? `SL: ${trade.stop_loss}` : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {trade.profit_target ? `TP: ${trade.profit_target}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {trade.actual_profit_loss != null ? (
                        <Typography
                          variant="body2"
                          color={trade.actual_profit_loss >= 0 ? 'success.main' : 'error.main'}
                          fontWeight="medium"
                        >
                          ${Number(trade.actual_profit_loss).toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={trade.trade_status}
                        size="small"
                        color={trade.trade_status === 'open' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <ExecutionLinkChip id={trade.ai_system_execution} />
                    </TableCell>
                    <TableCell>
                      <ExecutionLinkChip id={trade.closed_by_ai_system_execution} />
                    </TableCell>
                    <TableCell>
                      {trade.label ? (
                        <Chip label={trade.label} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TradesPanel;
